import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { ArrowRight, Link, Settings, Trash2, Edit2, Play, Pause, Bell, Zap, Activity, Search, ShieldCheck, Unlink, FileText, LogOut, RefreshCw, Server, AtSign, Languages } from 'lucide-react';
import { GlassButton } from '../components/ui/GlassButton';
import { Toast } from '../Toast';
import { ConfirmModal } from '../ConfirmModal';
import { LogWindow } from '../LogWindow';
import { IDFinderModal } from '../IDFinderModal';
import { EditRepasseModal } from '../components/EditRepasseModal';

interface Connection {
    id: number;
    from_chat: string;
    to_chat: string;
    from_title?: string;
    to_title?: string;
    active: boolean;
    replacement_url?: string;
    enable_link_replacement?: boolean;
    replacement_user?: string;
    enable_user_replacement?: boolean;
    enable_translation?: boolean;
    translation_language?: string;
}

interface RepasseProps {
    selectedAccountId: number | string | null;
    onOpenIDFinder: () => void;
}

export const Repasse: React.FC<RepasseProps> = ({ selectedAccountId, onOpenIDFinder }) => {
    const [connections, setConnections] = useState<Connection[]>([]);
    const [loading, setLoading] = useState(false);

    // Form State (apenas para criar novo)
    const [fromChat, setFromChat] = useState('');
    const [toChat, setToChat] = useState('');
    const [replacementURL, setReplacementURL] = useState('');
    const [enableLinkReplacement, setEnableLinkReplacement] = useState(false);
    const [replacementUser, setReplacementUser] = useState('');
    const [enableUserReplacement, setEnableUserReplacement] = useState(false);
    const [enableTranslation, setEnableTranslation] = useState(false);
    const [translationLanguage, setTranslationLanguage] = useState('');

    // Edit Modal State
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingConnection, setEditingConnection] = useState<Connection | null>(null);

    // Global Flood State
    const [globalFlood, setGlobalFlood] = useState<{ remaining: number, source: string, message: string } | null>(null);

    // Initial Check for Persistence
    useEffect(() => {
        checkGlobalFlood();
        const interval = setInterval(checkGlobalFlood, 60000); // Check every minute
        return () => clearInterval(interval);
    }, []);

    // Timer Effect
    useEffect(() => {
        if (!globalFlood) return;
        if (globalFlood.remaining <= 0) {
            setGlobalFlood(null);
            return;
        }
        const timer = setTimeout(() => {
            setGlobalFlood(prev => prev ? { ...prev, remaining: prev.remaining - 1 } : null);
        }, 1000);
        return () => clearTimeout(timer);
    }, [globalFlood]);

    const checkGlobalFlood = async () => {
        try {
            const res = await api.get('/tools/active-flood-wait');
            if (res.data.active && res.data.timestamp) {
                const created = new Date(res.data.timestamp).getTime();
                const now = new Date().getTime();
                const waitMs = res.data.wait_seconds * 1000;
                const expiry = created + waitMs;
                const remain = Math.floor((expiry - now) / 1000); // Seconds

                if (remain > 0) {
                    setGlobalFlood({ remaining: remain, source: res.data.source, message: res.data.message });
                } else {
                    setGlobalFlood(null);
                }
            }
        } catch (e) {
            console.error("Erro check flood:", e);
        }
    };

    // Modals
    const [logModal, setLogModal] = useState<{ isOpen: boolean, connId: number | null, title: string }>({ isOpen: false, connId: null, title: '' });
    const [localIDFinderOpen, setLocalIDFinderOpen] = useState(false);
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'info' | 'error' } | null>(null);

    useEffect(() => {
        if (selectedAccountId) {
            fetchConnections(selectedAccountId);
        } else {
            setConnections([]);
        }
    }, [selectedAccountId]);

    const fetchConnections = async (accId: number | string) => {
        try {
            const res = await api.get('/connections', { params: { account_id: accId } });
            setConnections(res.data);
        } catch (e) {
            console.error("Erro conexões", e);
        }
    };

    const createConnection = async () => {
        if (!selectedAccountId) {
            setToast({ msg: "Selecione ou crie uma conta primeiro.", type: "error" });
            return;
        }
        if (!fromChat || !toChat) {
            setToast({ msg: 'Por favor, preencha ambos os campos.', type: "error" });
            return;
        }

        setLoading(true);

        try {
            await api.post('/connections', {
                account_id: selectedAccountId,
                from_chat: fromChat,
                to_chat: toChat,
                replacement_url: replacementURL,
                enable_link_replacement: enableLinkReplacement,
                replacement_user: replacementUser,
                enable_user_replacement: enableUserReplacement,
                enable_translation: enableTranslation,
                translation_language: translationLanguage
            });
            setToast({ msg: "Conexão criada com sucesso!", type: "success" });
            fetchConnections(selectedAccountId);
            setFromChat('');
            setToChat('');
            setReplacementURL('');
            setEnableLinkReplacement(false);
            setReplacementUser('');
            setEnableUserReplacement(false);
            setEnableTranslation(false);
            setTranslationLanguage('');
        } catch (e: any) {
            const msg = e.response?.data?.error || e.response?.data?.detail || e.message;
            setToast({ msg: 'Erro: ' + msg, type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (conn: Connection) => {
        setEditingConnection(conn);
        setEditModalOpen(true);
    };

    const handleSaveEdit = async (data: {
        from_chat: string;
        to_chat: string;
        replacement_url: string;
        enable_link_replacement: boolean;
        replacement_user: string;
        enable_user_replacement: boolean;
        enable_translation: boolean;
        translation_language: string;
    }) => {
        if (!editingConnection) return;

        try {
            await api.put(`/connections/${editingConnection.id}`, data);
            setToast({ msg: "Conexão atualizada com sucesso!", type: "success" });
            if (selectedAccountId) fetchConnections(selectedAccountId);
            setEditModalOpen(false);
            setEditingConnection(null);
        } catch (e: any) {
            const msg = e.response?.data?.error || e.response?.data?.detail || e.message;
            setToast({ msg: 'Erro ao atualizar: ' + msg, type: "error" });
            throw e;
        }
    };

    const deleteConnection = async (id: number) => {
        try {
            await api.delete(`/connections/${id}`);
            setToast({ msg: "Repasse parado com sucesso.", type: 'info' });
            if (selectedAccountId) fetchConnections(selectedAccountId);
        } catch (e) {
            setToast({ msg: "Erro ao deletar conexão.", type: "error" });
        }
    };



    return (
        <div className="dashboard-view">
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            {/* Top Bar */}
            <div className="top-bar">
                <div>
                    <h1 className="page-title">Repasse</h1>

                </div>

                <div className="top-actions">
                    <GlassButton onClick={() => setLocalIDFinderOpen(true)} style={{ width: 'auto' }}>
                        <Search size={16} />
                        <span>Descobridor de ID</span>
                    </GlassButton>
                </div>
            </div>

            {/* Global Alerts */}
            {globalFlood && (
                <div className="flood-wait-box global-alert" style={{
                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(127, 29, 29, 0.2) 100%)',
                    border: '1px solid #ef4444',
                    borderRadius: '12px',
                    padding: '20px',
                    margin: '0 24px 24px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    boxShadow: 'none'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#ff6b6b', fontWeight: 'bold', fontSize: '16px' }}>
                        <ShieldCheck size={20} />
                        Bloqueios Ativos do Telegram
                    </div>

                    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <h4 style={{ margin: 0, color: '#fff', fontSize: '15px' }}>{globalFlood.source}</h4>
                                <p style={{ margin: '4px 0 0 0', color: '#ff8a8a', fontSize: '13px' }}>Bloqueio temporário (FloodWait)</p>
                            </div>
                            <Activity size={16} color="#ff6b6b" className="pulse-slow" />
                        </div>

                        <div style={{ fontSize: '36px', fontWeight: '800', color: '#fff', marginTop: '15px', fontFamily: 'monospace' }}>
                            {Math.floor(globalFlood.remaining / 3600)}:
                            {Math.floor((globalFlood.remaining % 3600) / 60).toString().padStart(2, '0')}:
                            {(globalFlood.remaining % 60).toString().padStart(2, '0')}
                        </div>

                        <div style={{ fontSize: '12px', color: '#aaa', marginTop: '8px' }}>
                            Liberando automaticamente...
                        </div>
                    </div>
                </div>
            )}

            {selectedAccountId ? (
                <>
                    {/* Stats Grid */}
                    <div className="stats-grid">
                        <div className="stat-card service-status">
                            <div className="stat-header">
                                <div className="icon-box blue-glow">
                                    <Activity size={24} color="#ff6a00" />
                                </div>
                                <span className="status-badge">ONLINE</span>
                            </div>
                            <div className="stat-content">
                                <span className="stat-label">Status do Serviço</span>
                                <span className="stat-value">Executando</span>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-header">
                                <div className="icon-box purple-glow">
                                    <Link size={24} color="#a78bfa" />
                                </div>
                            </div>
                            <div className="stat-content">
                                <span className="stat-label">Conexões Ativas</span>
                                <span className="stat-value">{connections.length}</span>
                            </div>
                        </div>
                    </div>

                    <div className="main-grid split-layout">
                        <div className="section-card connections-panel">
                            <div className="card-header">
                                <span className="card-title">Gerenciar Conexões</span>
                                <div className="table-actions">
                                    <div className="search-filter">
                                        <span>Filtrar...</span>
                                    </div>
                                </div>
                            </div>

                            {connections.length === 0 ? (
                                <div className="empty-state-pro">
                                    <div className="empty-icon-box">
                                        <Unlink size={32} color="#fff" />
                                    </div>
                                    <h3>Nenhuma conexão ativa</h3>
                                    <p>Seu dashboard está pronto para automatizar. Comece criando um novo repasse.</p>
                                </div>
                            ) : (
                                <div className="connections-list-pro">
                                    {connections.map(conn => (
                                        <div key={conn.id} className="connection-item-pro">
                                            <div className="conn-info">
                                                <div className="conn-point">
                                                    <span className="label">ORIGEM</span>
                                                    <span className="value" title={conn.from_title}>{conn.from_title || conn.from_chat}</span>
                                                </div>
                                                <div className="conn-arrow">
                                                    <ArrowRight size={16} />
                                                </div>
                                                <div className="conn-point">
                                                    <span className="label">DESTINO</span>
                                                    <span className="value" title={conn.to_title}>{conn.to_title || conn.to_chat}</span>
                                                </div>
                                            </div>
                                            <div className="conn-actions">
                                                <button className="action-icon" onClick={() => setLogModal({ isOpen: true, connId: conn.id, title: `Log #${conn.id}` })}>
                                                    <FileText size={16} />
                                                </button>
                                                <button className="action-icon edit" onClick={() => handleEditClick(conn)}>
                                                    <Edit2 size={16} />
                                                </button>
                                                <button className="action-icon delete" onClick={() => deleteConnection(conn.id)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="section-card form-panel">
                            <div className="card-header no-border">
                                <span className="card-title">Novo Repasse</span>
                            </div>


                            <div className="input-group-pro">
                                <label>ORIGEM</label>
                                <div className="input-wrapper">
                                    <LogOut size={16} />
                                    <input
                                        placeholder="ID do Canal ou @usuario"
                                        value={fromChat}
                                        onChange={(e) => setFromChat(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="input-group-pro">
                                <label>DESTINO</label>
                                <div className="input-wrapper">
                                    <ArrowRight size={16} />
                                    <input
                                        placeholder="ID do Canal ou @usuario"
                                        value={toChat}
                                        onChange={(e) => setToChat(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Link Replacement Settings */}
                            <div className="input-group-pro" style={{ marginTop: '20px', borderTop: '1px solid #333', paddingTop: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#fff' }}>
                                        <input
                                            type="checkbox"
                                            checked={enableLinkReplacement}
                                            onChange={(e) => setEnableLinkReplacement(e.target.checked)}
                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                        />
                                        Substituição de Links
                                    </label>

                                </div>

                                {enableLinkReplacement && (
                                    <div className="input-wrapper fade-in">
                                        <Link size={16} color="#aaa" />
                                        <input
                                            placeholder="https://seu-link-afiliado.com/..."
                                            value={replacementURL}
                                            onChange={(e) => setReplacementURL(e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* User Replacement Settings */}
                            <div className="input-group-pro" style={{ marginTop: '10px', borderTop: '1px solid #333', paddingTop: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#fff' }}>
                                        <input
                                            type="checkbox"
                                            checked={enableUserReplacement}
                                            onChange={(e) => setEnableUserReplacement(e.target.checked)}
                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                        />
                                        Substituição de @Usuario
                                    </label>
                                </div>

                                {enableUserReplacement && (
                                    <div className="input-wrapper fade-in">
                                        <AtSign size={16} color="#aaa" />
                                        <input
                                            placeholder="@NovoUsuario"
                                            value={replacementUser}
                                            onChange={(e) => setReplacementUser(e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Translation Settings */}
                            <div className="input-group-pro" style={{ marginTop: '10px', borderTop: '1px solid #333', paddingTop: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#fff' }}>
                                        <input
                                            type="checkbox"
                                            checked={enableTranslation}
                                            onChange={(e) => setEnableTranslation(e.target.checked)}
                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                        />
                                        Tradução Automática
                                    </label>
                                    <span style={{ fontSize: '10px', color: '#06b6d4', background: 'rgba(6, 182, 212, 0.1)', padding: '4px 8px', borderRadius: '4px', fontWeight: '600', letterSpacing: '0.05em' }}>IDIOMA</span>
                                </div>

                                {enableTranslation && (
                                    <div className="input-wrapper fade-in">
                                        <Languages size={16} color="#aaa" />
                                        <select
                                            value={translationLanguage}
                                            onChange={(e) => setTranslationLanguage(e.target.value)}
                                        >
                                            <option value="">Selecione o idioma</option>
                                            <option value="en">🇬🇧 Inglês</option>
                                            <option value="es">🇪🇸 Espanhol</option>
                                        </select>
                                    </div>
                                )}
                            </div>

                            <div className="form-actions">
                                <GlassButton
                                    className="w-full"
                                    onClick={createConnection}
                                    loading={loading}
                                >
                                    <Zap size={18} fill="currentColor" /> Criar Conexão
                                </GlassButton>
                            </div>


                        </div>
                    </div>
                </>
            ) : (
                <div className="empty-state-pro" style={{ marginTop: '100px' }}>
                    <div className="empty-icon-box">
                        <Server size={32} />
                    </div>
                    <h2>Selecione um Servidor</h2>
                    <p>Escolha ou adicione uma conta no menu lateral para gerenciar conexões.</p>
                </div>
            )
            }

            <LogWindow
                isOpen={logModal.isOpen}
                onClose={() => setLogModal({ ...logModal, isOpen: false })}
                connectionId={logModal.connId}
                connectionTitle={logModal.title}
            />

            {localIDFinderOpen && (
                <IDFinderModal
                    onClose={() => setLocalIDFinderOpen(false)}
                    accountId={selectedAccountId}
                    onSelectId={(id, target) => {
                        if (target === 'from') setFromChat(id);
                        if (target === 'to') setToChat(id);
                        setLocalIDFinderOpen(false);
                        setToast({ msg: `ID Aplicado no campo ${target === 'from' ? 'Origem' : 'Destino'}!`, type: 'success' });
                    }}
                />
            )}

            <EditRepasseModal
                isOpen={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false);
                    setEditingConnection(null);
                }}
                connection={editingConnection}
                onSave={handleSaveEdit}
            />
        </div >
    );
};
